import { Ban, CheckCircle2, Mail, RotateCcw, Search, Trash2, X } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { assistantService } from '../api/services/assistant.service'
import { doctorService } from '../api/services/doctor.service'
import { doctorSignupService } from '../api/services/doctor-signup.service'
import { userService } from '../api/services/user.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { ListPagination } from '../components/ui/list-pagination'
import { SelectPicker, type SelectOption } from '../components/ui/select-picker'
import { SoftDeleteConfirmDialog } from '../components/ui/soft-delete-confirm-dialog'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { useAuth } from '../context/AuthContext'
import {
  ASSISTANT_SALUTATION_OPTIONS,
  type AssistantCreatePayload,
  type AssistantDoctorLink,
  type AssistantUpdatePayload,
} from '../types/assistant'
import { USER_ROLES } from '../types/auth'

type AccountFilter = 'all' | 'active' | 'disabled'

const ACCOUNT_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Inactive' },
]

const createSchema = z.object({
  salutation: z.string().min(1),
  first_name: z.string().min(1, 'First name is required'),
  surname: z.string().min(1, 'Surname is required'),
  email: z.string().email('Valid email is required'),
  phone_number: z.string().optional(),
  position_id: z.string().optional(),
  doctor_id: z.string().optional(),
  can_create_cases: z.boolean(),
  can_set_global: z.boolean(),
  can_manage_review: z.boolean(),
  can_update_outcome: z.boolean(),
})

const editSchema = z.object({
  can_create_cases: z.boolean(),
  can_set_global: z.boolean(),
  can_manage_review: z.boolean(),
  can_update_outcome: z.boolean(),
})

type CreateFormValues = z.infer<typeof createSchema>
type EditFormValues = z.infer<typeof editSchema>

const emptyCreateForm: CreateFormValues = {
  salutation: 'mr',
  first_name: '',
  surname: '',
  email: '',
  phone_number: '',
  position_id: '',
  doctor_id: '',
  can_create_cases: false,
  can_set_global: false,
  can_manage_review: false,
  can_update_outcome: false,
}

export function DoctorAssistantsPage() {
  const { can, session } = useAuth()
  const isStaff =
    session?.role === USER_ROLES.SUPER_ADMIN ||
    session?.role === USER_ROLES.PROFILE_REVIEWER
  const [links, setLinks] = useState<AssistantDoctorLink[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [withTrashed, setWithTrashed] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [doctorFilter, setDoctorFilter] = useState('all')
  const [accountFilter, setAccountFilter] = useState<AccountFilter>('all')
  const [doctorOptions, setDoctorOptions] = useState<SelectOption[]>([
    { value: 'all', label: 'All doctors' },
  ])
  const [positionOptions, setPositionOptions] = useState<SelectOption[]>([])
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<AssistantDoctorLink | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<AssistantDoctorLink | null>(null)
  const [unlinkChoices, setUnlinkChoices] = useState<
    Array<{ link_id: string; doctor_id: string; doctor_name: string | null; doctor_email: string | null }>
  >([])
  const [unlinkDoctorId, setUnlinkDoctorId] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [meta, setMeta] = useState<PaginationMeta | null>(null)

  const createForm = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: emptyCreateForm,
  })

  const editForm = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      can_create_cases: false,
      can_set_global: false,
      can_manage_review: false,
      can_update_outcome: false,
    },
  })

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (search.trim()) count += 1
    if (isStaff && doctorFilter !== 'all') count += 1
    if (accountFilter !== 'all') count += 1
    if (withTrashed) count += 1
    return count
  }, [accountFilter, doctorFilter, isStaff, search, withTrashed])

  const clearFilters = () => {
    setSearch('')
    setSearchInput('')
    setDoctorFilter('all')
    setAccountFilter('all')
    setWithTrashed(false)
  }

  const loadDoctors = useCallback(async () => {
    if (!isStaff || !can('doctors-list')) {
      return
    }

    try {
      const response = await doctorService.list({ status: 'approved', per_page: 100 })
      const doctors = Array.isArray(response.data) ? response.data : []
      setDoctorOptions([
        { value: 'all', label: 'All doctors' },
        ...doctors.map((doctor) => ({
          value: doctor.id,
          label: `${doctor.first_name} ${doctor.surname}`.trim() || doctor.email,
        })),
      ])
    } catch {
      setDoctorOptions([{ value: 'all', label: 'All doctors' }])
    }
  }, [can, isStaff])

  const loadLinks = useCallback(async () => {
    try {
      const response = await assistantService.list({
        page,
        per_page: perPage,
        only_trashed: withTrashed || undefined,
        search: search.trim() || undefined,
        doctor_id: isStaff && doctorFilter !== 'all' ? doctorFilter : undefined,
        account_active:
          accountFilter === 'all' ? undefined : accountFilter === 'active',
      })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setLinks(items)
      setMeta(nextMeta)
    } catch {
      setLinks([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [accountFilter, doctorFilter, isStaff, page, perPage, search, withTrashed])

  useEffect(() => {
    void loadDoctors()
  }, [loadDoctors])

  // Positions come from the public signup form-data endpoint so both doctors and
  // super admins can populate the picker without the positions-list permission.
  useEffect(() => {
    void doctorSignupService
      .getFormData()
      .then((response) => {
        setPositionOptions(
          (response.data?.positions ?? []).map((position) => ({
            value: position.id,
            label: position.name,
          })),
        )
      })
      .catch(() => {
        setPositionOptions([])
      })
  }, [])

  useEffect(() => {
    void loadLinks()
  }, [loadLinks])

  useEffect(() => {
    setPage(1)
  }, [accountFilter, doctorFilter, search, withTrashed])

  const openCreate = () => {
    setEditing(null)
    createForm.reset(emptyCreateForm)
    setFormOpen(true)
  }

  const openEdit = (link: AssistantDoctorLink) => {
    setEditing(link)
    editForm.reset({
      can_create_cases: link.can_create_cases,
      can_set_global: link.can_set_global,
      can_manage_review: link.can_manage_review,
      can_update_outcome: link.can_update_outcome,
    })
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditing(null)
    createForm.reset(emptyCreateForm)
  }

  const onCreateSubmit = async (values: CreateFormValues) => {
    setSubmitting(true)

    const payload: AssistantCreatePayload = {
      salutation: values.salutation,
      first_name: values.first_name,
      surname: values.surname,
      email: values.email,
      phone_number: values.phone_number || undefined,
      position_id: values.position_id || undefined,
      doctor_id: isStaff ? values.doctor_id || undefined : undefined,
      can_create_cases: values.can_create_cases,
      can_set_global: values.can_set_global,
      can_manage_review: values.can_manage_review,
      can_update_outcome: values.can_update_outcome,
    }

    if (isStaff && !payload.doctor_id) {
      createForm.setError('doctor_id', { message: 'Doctor is required' })
      setSubmitting(false)
      return
    }

    try {
      await assistantService.create(payload)
      closeForm()
      await loadLinks()
    } catch {
      // toasted globally
    } finally {
      setSubmitting(false)
    }
  }

  const onEditSubmit = async (values: EditFormValues) => {
    if (!editing) {
      return
    }

    setSubmitting(true)

    const payload: AssistantUpdatePayload = {
      can_create_cases: values.can_create_cases,
      can_set_global: values.can_set_global,
      can_manage_review: values.can_manage_review,
      can_update_outcome: values.can_update_outcome,
    }

    try {
      await assistantService.update(editing.id, payload)
      closeForm()
      await loadLinks()
    } catch {
      // toasted globally
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return
    }

    setDeleting(true)

    try {
      if (isStaff && unlinkChoices.length > 0) {
        if (!unlinkDoctorId) {
          return
        }

        await assistantService.remove(deleteTarget.id, { doctorId: unlinkDoctorId })
        setDeleteTarget(null)
        setUnlinkChoices([])
        setUnlinkDoctorId('')
        await loadLinks()
        return
      }

      await assistantService.remove(deleteTarget.id, {
        skipErrorToast: isStaff,
      })
      setDeleteTarget(null)
      setUnlinkChoices([])
      setUnlinkDoctorId('')
      await loadLinks()
    } catch (error) {
      const axiosError = error as {
        response?: {
          status?: number
          data?: {
            errors?: {
              requires_doctor_selection?: boolean
              linked_doctors?: Array<{
                link_id: string
                doctor_id: string
                doctor_name: string | null
                doctor_email: string | null
              }>
            }
          }
        }
      }

      if (
        isStaff &&
        axiosError.response?.status === 409 &&
        axiosError.response.data?.errors?.requires_doctor_selection
      ) {
        const choices = axiosError.response.data.errors.linked_doctors ?? []
        setUnlinkChoices(choices)
        setUnlinkDoctorId(
          choices.find((choice) => choice.doctor_id === deleteTarget.doctor_id)?.doctor_id
            ?? choices[0]?.doctor_id
            ?? '',
        )
        return
      }
      // other errors toasted globally (unless skipErrorToast)
    } finally {
      setDeleting(false)
    }
  }

  const handleRestore = async (link: AssistantDoctorLink) => {
    try {
      await assistantService.restore(link.id)
      await loadLinks()
    } catch {
      // handled globally
    }
  }

  const handleDisable = async (link: AssistantDoctorLink) => {
    try {
      await assistantService.disable(link.id)
      await loadLinks()
    } catch {
      // handled globally
    }
  }

  const handleEnable = async (link: AssistantDoctorLink) => {
    try {
      await assistantService.enable(link.id)
      await loadLinks()
    } catch {
      // handled globally
    }
  }

  const handleResendCredentials = async (link: AssistantDoctorLink) => {
    setResendingId(link.id)

    try {
      await userService.resendCredentials(link.assistant_id)
    } catch {
      // handled globally
    } finally {
      setResendingId(null)
    }
  }

  const displayName = (link: AssistantDoctorLink) => {
    const assistant = link.assistant
    if (!assistant) {
      return link.assistant_id
    }
    return `${assistant.first_name} ${assistant.surname}`.trim()
  }

  const doctorName = (link: AssistantDoctorLink) => {
    const doctor = link.doctor
    if (!doctor) {
      return '—'
    }
    return `${doctor.first_name} ${doctor.surname}`.trim() || doctor.email
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">
              {isStaff ? 'Assistants' : 'My assistants'}
            </h1>
            <p className="mt-1 text-sm text-ink-600">
              {isStaff
                ? 'View assistants across all doctors. Filter by doctor, account status, name, or email.'
                : 'Create Assistant accounts and set per-link capabilities. Strong passwords are auto-generated and emailed.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-600"
                checked={withTrashed}
                onChange={(event) => setWithTrashed(event.target.checked)}
              />
              Show Deleted
            </label>
            <PermissionGate permission="assistants-create">
              <Button onClick={openCreate}>Add Assistant</Button>
            </PermissionGate>
          </div>
        </div>

        <section className="mb-6 rounded-xl border border-parch-100 bg-white p-4 shadow-card sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-ink-900">Filters</h2>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-semibold text-green-700">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-stretch gap-2">
            <div className="relative min-w-[12rem] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
              <Input
                className="pl-9"
                placeholder="Search name, email…"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setSearch(searchInput.trim())
                  }
                }}
              />
            </div>
            <Button onClick={() => setSearch(searchInput.trim())}>Search</Button>
            {activeFilterCount > 0 && (
              <Button variant="secondary" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>

          <div
            className={`mt-5 grid gap-4 sm:grid-cols-2 ${isStaff ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}
          >
            {isStaff && (
              <div>
                <FieldLabel>Doctor</FieldLabel>
                <SelectPicker
                  options={doctorOptions}
                  value={doctorFilter}
                  onChange={setDoctorFilter}
                  placeholder="All doctors"
                />
              </div>
            )}
            <div>
              <FieldLabel>Status</FieldLabel>
              <SelectPicker
                options={ACCOUNT_OPTIONS}
                value={accountFilter}
                onChange={(value) => setAccountFilter(value as AccountFilter)}
                placeholder="All"
              />
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  {isStaff && <th className="px-4 py-3 font-medium">Doctor</th>}
                  <th className="px-4 py-3 font-medium">Email</th>
                  {/* TEMP: capability columns hidden for now
                  <th className="px-4 py-3 font-medium">Create cases</th>
                  <th className="px-4 py-3 font-medium">Set global</th>
                  <th className="px-4 py-3 font-medium">Manage review</th>
                  <th className="px-4 py-3 font-medium">Update outcome</th>
                  */}
                  <th className="px-4 py-3 font-medium">Account</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && links.length === 0 ? (
                  <tr>
                    <td colSpan={isStaff ? 6 : 5} className="px-4 py-6 text-sm text-ink-500">
                      No assistants found.
                    </td>
                  </tr>
                ) : (
                  links.map((link) => (
                    <tr
                      key={link.id}
                      className={`border-b border-parch-50 last:border-0 ${link.deleted_at ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-3">
                        {displayName(link)}
                        {link.deleted_at && (
                          <span className="ml-2 text-xs text-danger">Deleted</span>
                        )}
                      </td>
                      {isStaff && <td className="px-4 py-3">{doctorName(link)}</td>}
                      <td className="px-4 py-3">{link.assistant?.email ?? '—'}</td>
                      {/* TEMP: capability columns hidden for now
                      <td className="px-4 py-3">{link.can_create_cases ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">{link.can_set_global ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">{link.can_manage_review ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">{link.can_update_outcome ? 'Yes' : 'No'}</td>
                      */}
                      <td className="px-4 py-3">
                        {link.assistant?.is_active ? 'Active' : 'Disabled'}
                      </td>
                      <td className="px-4 py-3">
                        <TableIconActions>
                          {/* TEMP: assistant capability flags editing hidden for now
                          {can('assistants-update') && !link.deleted_at && (
                            <IconActionButton
                              icon={Pencil}
                              label="Edit Flags"
                              onClick={() => openEdit(link)}
                            />
                          )}
                          */}
                          {can('users-resend-credentials') && !link.deleted_at && (
                            <IconActionButton
                              icon={Mail}
                              label="Resend Creds Email"
                              disabled={resendingId === link.id}
                              onClick={() => void handleResendCredentials(link)}
                            />
                          )}
                          {can('assistants-disable') && !link.deleted_at && link.assistant?.is_active && (
                            <IconActionButton
                              icon={Ban}
                              label="Disable"
                              onClick={() => void handleDisable(link)}
                            />
                          )}
                          {can('assistants-disable') &&
                            !link.deleted_at &&
                            link.assistant &&
                            !link.assistant.is_active && (
                              <IconActionButton
                                icon={CheckCircle2}
                                label="Enable"
                                onClick={() => void handleEnable(link)}
                              />
                            )}
                          {can('assistants-delete') && !link.deleted_at && (
                            <IconActionButton
                              icon={Trash2}
                              label="Delete"
                              variant="destructive"
                              onClick={() => setDeleteTarget(link)}
                            />
                          )}
                          {can('assistants-restore') && link.deleted_at && (
                            <IconActionButton
                              icon={RotateCcw}
                              label="Restore"
                              onClick={() => void handleRestore(link)}
                            />
                          )}
                        </TableIconActions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n)
            setPage(1)
          }}
        />

        <SoftDeleteConfirmDialog
          open={deleteTarget !== null && unlinkChoices.length === 0}
          entityLabel="assistant"
          itemName={deleteTarget ? displayName(deleteTarget) : ''}
          onCancel={() => {
            setDeleteTarget(null)
            setUnlinkChoices([])
            setUnlinkDoctorId('')
          }}
          onConfirm={() => void handleDeleteConfirm()}
          confirming={deleting}
        />

        {deleteTarget !== null && unlinkChoices.length > 0 && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">Unlink assistant</h2>
              <p className="mt-2 text-sm text-ink-600">
                {displayName(deleteTarget)} is linked to multiple doctors. Choose which doctor
                to unlink. Other doctor links stay active.
              </p>
              <div className="mt-4">
                <FieldLabel required>Doctor</FieldLabel>
                <SelectPicker
                  options={unlinkChoices.map((choice) => ({
                    value: choice.doctor_id,
                    label:
                      choice.doctor_name ||
                      choice.doctor_email ||
                      choice.doctor_id,
                  }))}
                  value={unlinkDoctorId}
                  onChange={setUnlinkDoctorId}
                  placeholder="Select doctor"
                />
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDeleteTarget(null)
                    setUnlinkChoices([])
                    setUnlinkDoctorId('')
                  }}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={deleting || !unlinkDoctorId}
                  onClick={() => void handleDeleteConfirm()}
                >
                  {deleting ? 'Unlinking…' : 'Unlink'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {formOpen && !editing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-xl rounded-lg bg-white p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">Create Assistant</h2>
              <p className="mt-1 text-sm text-ink-600">
                New emails create an assistant and email login credentials. An existing assistant
                email links that person to this doctor without creating a second account.
              </p>

              <form className="mt-5 space-y-3" onSubmit={createForm.handleSubmit(onCreateSubmit)}>
                <FormRequiredLegend />

                {isStaff && (
                  <div>
                    <FieldLabel required>Doctor</FieldLabel>
                    <SelectPicker
                      options={doctorOptions.filter((option) => option.value !== 'all')}
                      value={createForm.watch('doctor_id') || ''}
                      onChange={(value) => createForm.setValue('doctor_id', value)}
                      placeholder="Select doctor"
                    />
                    {createForm.formState.errors.doctor_id && (
                      <p className="mt-1 text-xs text-danger">
                        {createForm.formState.errors.doctor_id.message}
                      </p>
                    )}
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <FieldLabel required>First name</FieldLabel>
                    <Input {...createForm.register('first_name')} />
                    {createForm.formState.errors.first_name && (
                      <p className="mt-1 text-xs text-danger">
                        {createForm.formState.errors.first_name.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <FieldLabel required>Surname</FieldLabel>
                    <Input {...createForm.register('surname')} />
                    {createForm.formState.errors.surname && (
                      <p className="mt-1 text-xs text-danger">
                        {createForm.formState.errors.surname.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <FieldLabel required>Salutation</FieldLabel>
                    <select
                      className="w-full rounded-md border border-parch-200 px-3 py-2 text-sm"
                      {...createForm.register('salutation')}
                    >
                      {ASSISTANT_SALUTATION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <FieldLabel optional>Position</FieldLabel>
                    <SelectPicker
                      options={positionOptions}
                      value={createForm.watch('position_id') || ''}
                      onChange={(value) => createForm.setValue('position_id', value)}
                      placeholder="Select position"
                    />
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <FieldLabel required>Email</FieldLabel>
                    <Input type="email" {...createForm.register('email')} />
                    {createForm.formState.errors.email && (
                      <p className="mt-1 text-xs text-danger">
                        {createForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <FieldLabel optional>Phone number</FieldLabel>
                    <Input {...createForm.register('phone_number')} />
                  </div>
                </div>

                {/* TEMP: assistant capability flags UI hidden for now
                <div className="rounded-md border border-parch-100 bg-parch-50 p-3">
                  <p className="mb-2 text-sm font-medium text-ink-700">Capabilities</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" {...createForm.register('can_create_cases')} />
                      Can create cases
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" {...createForm.register('can_set_global')} />
                      Can set global
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" {...createForm.register('can_manage_review')} />
                      Can manage review
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" {...createForm.register('can_update_outcome')} />
                      Can update outcome
                    </label>
                  </div>
                </div>
                */}

                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="secondary" onClick={closeForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving…' : 'Create Assistant'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* TEMP: edit assistant flags modal hidden for now
        {formOpen && editing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">Edit assistant flags</h2>
              <p className="mt-1 text-sm text-ink-600">
                {displayName(editing)} — update pivot capabilities for this link.
              </p>

              <form className="mt-6 space-y-4" onSubmit={editForm.handleSubmit(onEditSubmit)}>
                <div className="space-y-2 rounded-md border border-parch-100 bg-parch-50 p-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" {...editForm.register('can_create_cases')} />
                    Can create cases
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" {...editForm.register('can_set_global')} />
                    Can set global
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" {...editForm.register('can_manage_review')} />
                    Can manage review
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" {...editForm.register('can_update_outcome')} />
                    Can update outcome
                  </label>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={closeForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving…' : 'Save Flags'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
        */}
      </div>
    </AppShell>
  )
}
